import { formatPhone } from './format';
import type { LiveTranscript } from './useWorkplaceState';

/**
 * Лента реплик разговора (ТЗ §2.11).
 *
 * Живёт отдельным компонентом, потому что нужна в ДВУХ местах: в окне идущего
 * разговора и на экране «Обработки» после него. В обработке по ней заполняют
 * итог — человек смотрит в текст, а не вспоминает. Копия ленты рядом с
 * оригиналом разъехалась бы на первой же правке подписей ролей.
 *
 * ⭐ Пустая лента обязана называть причину. Без неё молчание экрана читается
 * как «система меня не слышит», и человек начинает говорить громче вместо
 * того, чтобы вести разговор. Поэтому состояния названы словами: идёт, ждём
 * записи, распознавание отказало, лента выключена, разговор завершён.
 */
export function LiveRibbon({
  transcript,
  operatorName,
  clientPhone,
}: {
  transcript: LiveTranscript | null;
  operatorName: string;
  clientPhone: string;
}) {
  const replicas = transcript?.replicas ?? [];
  const status = transcript?.status ?? 'waiting';

  return (
    <div
      data-tel="live-ribbon"
      className="mt-3 max-h-72 min-h-40 overflow-y-auto rounded-ops-md p-3"
      style={{ background: 'var(--ops-surface-2)', border: '1px solid var(--ops-border)' }}
    >
      <div className="flex items-center gap-2">
        <span
          className="h-2 w-2 rounded-ops-pill"
          style={{ background: status === 'live' ? 'var(--ops-success)' : 'var(--ops-text-tertiary)' }}
          aria-hidden
        />
        <span
          className="text-ops-micro font-bold uppercase tracking-[0.08em]"
          style={{ color: 'var(--ops-text-tertiary)' }}
        >
          Расшифровка · {ribbonWord(status)}
        </span>
      </div>

      {replicas.length === 0 ? (
        <p className="mt-2 text-ops-caption" style={{ color: 'var(--ops-text-secondary)' }}>
          {emptyWord(status)}
        </p>
      ) : null}

      {replicas.some((r) => r.role === 'mixed') ? (
        <p className="mt-2 text-ops-micro" style={{ color: 'var(--ops-text-tertiary)' }}>
          Голоса не разделились: микрофон слышит собеседника через динамик. Кто именно
          сказал фразу, из этой записи не следует — в наушниках или у уха разделение вернётся.
        </p>
      ) : null}

      <div className="mt-2 space-y-1.5">
        {replicas.map((replica, index) => (
          <div key={`${replica.t_from}-${index}`} className="text-ops-caption">
            <span className="mr-2 font-semibold" style={{ color: roleColor(replica.role) }}>
              {roleLabel(replica.role, operatorName, clientPhone)}
            </span>
            <span style={{ color: 'var(--ops-text)' }}>{replica.text}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Подпись реплики — ИМЕНАМИ, а не ролями.
 *
 * ⭐ Роль читается по-разному с двух сторон трубки. Владелец на приёмке звонил
 * в поддержку оператора связи: там «оператор» — робот на том конце, а «клиент» —
 * он сам. Наши подписи говорят обратное (оператор — тот, кто в кабинете) — и
 * человек решил, что система переставила роли, хотя дорожки были разделены
 * верно. Имя менеджера против его собственных слов и номер собеседника против
 * его — второго толкования не допускают.
 *
 * «Разговор» — это честное «голоса не разделились»: так бывает на громкой
 * связи, когда телефон отдаёт в микрофон то, что играет. Уверенное имя в такой
 * ленте было бы неправдой.
 */
function roleLabel(role: string, operatorName: string, clientPhone: string): string {
  if (role === 'client') return `${clientPhone ? formatPhone(clientPhone) : 'Собеседник'}:`;
  // Голоса слиплись (эхо динамика) — честно говорим, что не разобрать, кто
  // именно сказал: подпись «Разговор» владелец 13.08 прочитал как имя.
  if (role === 'mixed') return 'Не разобрать кто:';

  return `${firstName(operatorName)}:`;
}

/**
 * Из «Юрий Жариков — браузер» получается «Юрий»: в ленте подпись повторяется
 * у каждой реплики, и длинная съедает саму реплику на телефоне.
 */
function firstName(name: string): string {
  const clean = name.split('—')[0].trim();

  return clean.split(/\s+/)[0] || 'Оператор';
}

function roleColor(role: string): string {
  if (role === 'client') return 'var(--ops-primary)';
  if (role === 'mixed') return 'var(--ops-text-secondary)';

  return 'var(--ops-success)';
}

function ribbonWord(status: LiveTranscript['status']): string {
  switch (status) {
    case 'live':
      return 'идёт в реальном времени';
    case 'ended':
      return 'разговор завершён';
    case 'unavailable':
      return 'недоступна';
    case 'off':
      return 'выключена';
    default:
      return 'ждём начала записи';
  }
}

function emptyWord(status: LiveTranscript['status']): string {
  switch (status) {
    case 'ended':
      return 'Разговор закончился, а распознать по ходу ничего не успели. Полный текст появится в журнале.';
    case 'unavailable':
      return 'Расшифровка недоступна — распознавание не отвечает. На разговор это не влияет, полный текст появится в журнале после звонка.';
    case 'off':
      return 'Живая расшифровка выключена в настройках. Полный текст появится в журнале после разговора.';
    case 'live':
      return 'Пока тихо — первые реплики появятся через несколько секунд после начала разговора.';
    default:
      return 'Запись ещё не началась: она включается, когда собеседник снимет трубку.';
  }
}
